import { Avatar, Skeleton, Typography, IconButton } from "@mui/material";
import InfiniteScroll from "react-infinite-scroll-component";
import { useDispatch, useSelector } from "react-redux";
import { fetchChatMedia, selectChatLoading } from "../../../slices/chat";
import { format, isToday, isYesterday, parseISO } from 'date-fns';
import { forwardRef, useEffect, useState } from "react";
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadImageApi from "../../../../services/downloadImageService";
import { ImageIcon, PdfIcon,DocumentIcon, FileIcon } from "../../../../assets/common-svg/CommonSvg";
import { notify } from "../../../utils/methods/notification";

const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const groupMessagesByDate = (messages) => {
    const sortedMessages = [...messages].sort((a, b) =>
        new Date(b.created_at) - new Date(a.created_at)
    );

    const grouped = {};

    sortedMessages.forEach(message => {
        const date = parseISO(message.created_at);
        let dateKey;

        if (isToday(date)) {
            dateKey = 'Today';
        } else if (isYesterday(date)) {
            dateKey = 'Yesterday';
        } else {
            dateKey = format(date, 'MMMM d, yyyy');
        }

        if (!grouped[dateKey]) {
            grouped[dateKey] = [];
        }
        grouped[dateKey].push(message);
    });

    return grouped;
};

const ChatHistory = forwardRef(({ messages, hasMore, fetchMoreData }, ref) => {
    const dispatch = useDispatch()
    const isLoading = useSelector(selectChatLoading)
    const [mediaMap, setMediaMap] = useState({});
    const [mediaLoading, setMediaLoading] = useState({});
    const [expandedMedia, setExpandedMedia] = useState({});

    const getAllMedia = async (id) => {
        setMediaLoading(prev => ({ ...prev, [id]: true }));
        try {
            const res = await dispatch(fetchChatMedia({ messege_id: id })).unwrap();
            if (res?.status === 200) {
                setMediaMap(prev => ({ ...prev, [id]: res.data.media }));
            }
        } catch (err) {
            console.error("Error fetching media:", err);
        } finally {
            setMediaLoading(prev => ({ ...prev, [id]: false }));
        }
    };

    const handleShowMore = (messageId) => {
        setExpandedMedia(prev => ({
            ...prev,
            [messageId]: !prev[messageId]
        }));
    };
    const handleDownload = async (mediaItem) => {
        try {
            const imgRes = await DownloadImageApi.getDownloadedImageul(mediaItem.id);

            // Fetch the image data and create a download link
            const response = await fetch(imgRes?.data || "");
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.download = mediaItem.file_name || 'download';
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();

            // Clean up
            setTimeout(() => {
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            }, 100);

        } catch (error) {
            notify.error('Download failed. Please try again later.')
        }
    };


    useEffect(() => {
        messages.forEach((msg) => {
            if (msg.message_type === 2 && !mediaMap[msg.id] && !mediaLoading[msg.id]) {
                getAllMedia(msg.id);
            }
        });
    }, [messages]);

    const renderLoadingSkeleton = () => {
        return Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className={`flex mb-4 ${index % 2 === 0 ? 'justify-end' : 'justify-start'}`}>
                <div className={`flex max-w-[80%] ${index % 2 === 0 ? 'flex-row-reverse' : ''}`}>
                    <div className={`mx-2 flex flex-col ${index % 2 === 0 ? 'items-end' : 'items-start'}`}>
                        <Skeleton variant="text" width={80} height={24} className="mb-1" />
                        <Skeleton variant="text" width={60} height={20} className="mb-1" />
                        <Skeleton variant="rectangular" width={200} height={80} className="rounded-t-xl rounded-br-xl" />
                        <Skeleton variant="text" width={60} height={20} className="mt-1" />
                    </div>
                </div>
            </div>
        ));
    };

    const renderMediaItems = (mediaItems, messageId) => {
        const isExpanded = expandedMedia[messageId];
        const itemsToShow = isExpanded ? mediaItems : mediaItems.slice(0, 2);
        const hasMoreItems = mediaItems.length > 2;

        return (
            <div className="mt-2">
                {itemsToShow.map((mediaItem) => {
                    return (
                        <div
                            key={mediaItem.id}
                            className="border rounded p-2 bg-white mb-2 relative"
                            style={{
                                filter: "none"
                            }}
                        >
                            {mediaItem?.file_type.startsWith('image/') ? (
                                <div className="flex gap-3">
                                    <ImageIcon />
                                    <span className="max-w-[150px] max-h-[150px] object-contain">
                                        {mediaItem.file_name}
                                    </span>
                                    {/* <img
                                        src={mediaItem.file_path}
                                        alt={mediaItem.file_name}
                                        className="max-w-[200px] max-h-[200px] object-contain"
                                    /> */}
                                    <div className="">
                                        <IconButton
                                            onClick={() => handleDownload(mediaItem)}
                                            sx={{
                                                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                                                }
                                            }}
                                        >
                                            <DownloadIcon color="primary" />
                                        </IconButton>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex items-center gap-2">
                                    {(() => {
                                        const fileName = mediaItem?.file_name || ""; // in case name differs
                                        const extension = fileName?.split('.')?.pop()?.toLowerCase();
                                        if (['pdf'].includes(extension)) {
                                        return <PdfIcon width={25} height={25} />;
                                        } else if (['doc', 'docx'].includes(extension)) {
                                        return <DocumentIcon width={25} height={25} />; // Replace with your document icon component
                                        } else {
                                        return <FileIcon width={25} height={25} />; // fallback icon
                                        }
                                    })()}
                                    <div>
                                        <Typography variant="body2" className="truncate max-w-[150px]">
                                            {mediaItem.file_name}
                                        </Typography>
                                        <Typography variant="caption" color="textSecondary">
                                            {formatFileSize(mediaItem.file_size)}
                                        </Typography>
                                    </div>
                                    <IconButton
                                        onClick={() => handleDownload(mediaItem)}
                                        size="small"
                                        sx={{ ml: 'auto' }}
                                    >
                                        <DownloadIcon fontSize="small" color="primary" />
                                    </IconButton>
                                </div>
                            )}
                        </div>
                    );
                })}

                {/* Show More/Show Less buttons remain the same */}
                {hasMoreItems && !isExpanded && (
                    <div
                        className="text-blue-500 text-sm cursor-pointer flex items-center"
                        onClick={() => handleShowMore(messageId)}
                    >
                        <VisibilityIcon fontSize="small" sx={{ mr: 0.5 }} />
                        Show {mediaItems.length - 2} more
                    </div>
                )}

                {isExpanded && hasMoreItems && (
                    <div
                        className="text-blue-500 text-sm cursor-pointer"
                        onClick={() => handleShowMore(messageId)}
                    >
                        Show less
                    </div>
                )}
            </div>
        );
    };

    const renderSkeleton = () => {
        return Array(2).fill(0).map((_, index) => (
            <div key={`skeleton-${index}`} className='pt-3 pb-3 border-b flex gap-2 items-center justify-between'>
                <div className='flex gap-2 items-center w-full'>
                    <div className="w-full">
                        <Skeleton variant="text" width="90%" height={20} />
                        <Skeleton variant="text" width="60%" height={15} />
                        <Skeleton variant="text" width="90%" height={20} />
                    </div>
                </div>
            </div>
        ));
    };

    const renderGroupedMessages = () => {
        const grouped = groupMessagesByDate(messages);
        return Object.entries(grouped).map(([date, dateMessages]) => (
            <div key={date} className="mb-4">
                <div className="flex justify-center my-4">
                    <div className="bg-gray-200 px-3 py-1 rounded-full text-xs text-gray-600">
                        {date}
                    </div>
                </div>

                {[...dateMessages].sort((a, b) => new Date(a.created_at) - new Date(b.created_at)).map((chat) => (
                    <div
                        key={chat.id}
                        className={`flex mb-4 ${chat.type === 0 ? 'justify-end' : 'justify-start'}`}
                    >
                        <div className={`flex max-w-[80%] ${chat.type === 0 ? 'flex-row-reverse' : ''}`}>
                            <div className={`mx-2 flex flex-col ${chat.type === 0 ? 'items-end' : 'items-start'}`}>
                                <div className="flex flex-col">
                                    <Typography variant="subtitle2" className={`font-semibold capitalize  ${chat.type === 0 ? 'text-end' : 'text-start'}`}>
                                        {chat.name}
                                    </Typography>
                                    <Typography variant="caption"
                                        className={`text-gray-500 capitalize ${chat.type === 0 ? 'text-end' : 'text-start'}`}>
                                        {chat.role}
                                    </Typography>
                                </div>
                                <div className={`mt-1 px-3 py-2 rounded-t-xl rounded-br-xl bg-[#F0F1F5] text-[#0C0C0C]`} style={{ wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                                    {chat.messages.map((msg, msgIndex) => (
                                        <Typography
                                            key={msgIndex}
                                            variant="body2"
                                            style={{
                                                wordBreak: 'break-word',
                                                whiteSpace: 'pre-wrap',
                                                overflowWrap: 'break-word'
                                            }}
                                        >
                                            {msg.split(' ').map((word, i) =>
                                                word.startsWith('@') ? (
                                                    <span
                                                        key={i}
                                                        className="font-semibold text-black-300"
                                                        style={{
                                                            wordBreak: 'break-word',
                                                            display: 'inline'
                                                        }}
                                                    >
                                                        {word}{' '}
                                                    </span>
                                                ) : (
                                                    <span
                                                        key={i}
                                                        style={{
                                                            wordBreak: 'break-word',
                                                            whiteSpace: 'pre-wrap',
                                                            display: 'inline'
                                                        }}
                                                    >
                                                        {word}{' '}
                                                    </span>
                                                )
                                            )}
                                        </Typography>
                                    ))}

                                    {chat.media_url && (
                                        <div className="mt-2">
                                            {chat.media_url.type === 'image/png' ? (
                                                <img
                                                    src={chat.media_url?.file_path}
                                                    alt="media"
                                                    className="max-w-[200px] max-h-[200px] object-contain"
                                                />
                                            ) : (
                                                <div className="border rounded p-2 bg-white">
                                                    <div className="flex items-center gap-2">
                                                        <Avatar sx={{ bgcolor: '#f0f1f5', color: '#666', width: 40, height: 40 }}>
                                                            {chat.media_url.type.split('.').pop().toUpperCase()}
                                                        </Avatar>
                                                        <div>
                                                            <Typography variant="body2" className="truncate max-w-[150px]">
                                                                {chat.media_url.type.split('/').pop()}
                                                            </Typography>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {chat.message_type === 2 && (
                                        mediaLoading[chat.id] ? (
                                            renderSkeleton()
                                        ) : (
                                            mediaMap[chat.id] && renderMediaItems(mediaMap[chat.id], chat.id)
                                        )
                                    )}
                                </div>
                                <Typography variant="caption" className="text-gray-500 mt-2">
                                    {chat.time}
                                </Typography>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        ));
    };

    return (
        <div
            ref={ref}
            id="scrollableDiv"
            className="flex-1 overflow-y-auto p-3 bg-white"
            style={{
                display: 'flex',
                flexDirection: 'column-reverse',
                height: '100%'
            }}
        >
            {isLoading ? (
                renderLoadingSkeleton()
            ) : (
                <InfiniteScroll
                    dataLength={messages.length}
                    next={fetchMoreData}
                    style={{ display: 'flex', flexDirection: 'column-reverse' }}
                    inverse={true}
                    hasMore={hasMore}
                    loader={
                        <div className="flex justify-center py-4">
                            <Skeleton variant="circular" width={24} height={24} />
                        </div>
                    }
                    scrollableTarget="scrollableDiv"
                >
                    {renderGroupedMessages()}
                </InfiniteScroll>
            )}
        </div>
    )
});

export default ChatHistory;